import type { DisplayState, StateConfig, StateDefinition } from './state.types'

/**
 * Interface for status options derived from state configuration
 */
export interface StatusOption {
  value: string
  label: string
  color?: string
  progressionLevel?: number
}

/**
 * Selects a dictionary of state definitions from the state configuration.
 *
 */
export function selectStateDictionary(states: StateConfig['states'] | undefined): Record<string, StateDefinition> {
  if (!states) {
    return {}
  }

  return states.reduce((dictionary, state) => {
    return { ...dictionary, [state.name]: state }
  }, {} as Record<string, StateDefinition>)
}

/**
 * Selects a map of order statuses to their display state
 * @param states - Array of state definitions
 * @returns A record where keys are status names and values are display state objects
 */
export function selectDisplayStateByStatus(
  states: StateConfig['states'] | undefined,
): Record<string, DisplayState | null> {
  if (!states) {
    return {}
  }

  return states.reduce((acc, state) => {
    acc[state.name] = state.displayState
    return acc
  }, {} as Record<string, DisplayState | null>)
}

/**
 * Derives status options from the state configuration
 * Filters states that can be manually transitioned to (canForceTransition: true)
 * and returns them as options sorted by progression level
 *
 * @param states - The state configuration
 * @returns An array of status options
 */
export function deriveStatusOptions(states: StateConfig['states'] | undefined): StatusOption[] {
  if (!states) {
    return []
  }

  return states
    .filter(state => state.canForceTransition === true)
    .map(state => ({
      value: state.name,
      label: state.displayState?.stateDisplayName || state.name,
      color: state.displayState?.color,
      progressionLevel: state.progressionLevel,
    }))
    // Sort by progression level
    .sort((a, b) => (a.progressionLevel || 0) - (b.progressionLevel || 0))
}
